import { cx } from "@linaria/core";
import type { ReactNode } from "react";
import React, { useCallback, useState } from "react";

import type {
  WPAsyncPageGroupDescriptor,
  WPPageDescriptor,
  WPPageGroupDescriptor,
} from "../../../../models/WPPageDescriptor";
import { isPageDescriptor } from "../../../../utils/guards/isPageDescriptor";
import { CSSRefTransition } from "../../../components/atoms/transition.css";
import { useActivePagePath } from "../../hooks/useActivePagePath";
import type { AppPage } from "../../hooks/usePageFromRouter";
import { LinkGroup } from "../SubNavigation/LinkGroup";
import { MobileLinkItem } from "../SubNavigation/MobileLinkItem";

import * as styles from "./styles/SubHeaderNav";

type Props = {
  page: AppPage | undefined;
  toggled: boolean;

  onClose(): void;
};

export function SubHeaderNav({
  page,
  toggled,
  onClose,
}: Props): ReactNode | null {
  if (!page?.isAuthorized || !page.subNavigation) {
    return null;
  }

  return (
    <CSSRefTransition
      in={toggled}
      timeout={styles.overlayTransitionDurationMs}
      mountOnEnter
      unmountOnExit
    >
      {(ref) => (
        <div ref={ref} className={styles.overlayCls} onClick={onClose}>
          <div
            className={cx(
              styles.popperCls,
              page?.product && "withProductStyling",
            )}
            onClick={(evt) => {
              evt.stopPropagation();
            }}
          >
            <ul className={styles.listCls}>
              {page.subNavigation?.map((it) => (
                <SubNavigationItem key={it.path} model={it} onClose={onClose} />
              ))}
            </ul>
          </div>
        </div>
      )}
    </CSSRefTransition>
  );
}

type ItemProps = {
  model: WPPageDescriptor | WPPageGroupDescriptor | WPAsyncPageGroupDescriptor;
  onClose(): void;
};

function SubNavigationItem({ model, onClose }: ItemProps): ReactNode {
  const activePagePath = useActivePagePath();

  const [isExpandedGroup, setExpandedGroup] = useState<boolean>(
    activePagePath.includes(model.path),
  );

  // Links should behave differently when the SubNavigation is rendered as a
  // SubHeaderNav. In particular Group links should simply toggle its expanded
  // state and regular links should close the overlay on navigate.
  const handleLinkClick = useCallback(
    (evt: React.MouseEvent<HTMLAnchorElement>, isGroupLink?: boolean) => {
      if (isGroupLink) {
        evt.preventDefault();
        setExpandedGroup((prev) => !prev);
      } else {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <li>
      {isPageDescriptor(model) ? (
        <MobileLinkItem onClick={handleLinkClick} item={model} />
      ) : (
        <LinkGroup
          forceExpanded={isExpandedGroup}
          item={model}
          onLinkClick={handleLinkClick}
          Link={MobileLinkItem}
        />
      )}
    </li>
  );
}
